/* eslint-disable */
import React from "react";
import { Box, Button, ButtonGroup, Grid, InputAdornment } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import esLocale from "date-fns/locale/es";

import MDTypography from "components/MDTypography";
import CancelButton from "components/common/CancelButton";
import AcceptButton from "components/common/AcceptButton";
import AppNumberInput from "components/common/AppNumberInput";
import AppFormContext from "components/common/AppFormContext";
import { segmentedButtonSx } from "components/common/buttonStyles";

import FormulaService from "services/formulaService";
import LocalMachineService from "services/localMachineService";
import VisitTransactionService from "services/visitTransactionService";

import { hasRent } from "utils/rent";
import {
  dateOnlyFromDateTime,
  getDefaultOperationDate,
  isBeforeMinOperationDate,
  isFutureOperationDate,
  isValidOperationDate,
  parseDateOnlyToLocalDate,
  toLocalDateOnlyString,
} from "utils/operationDate";

const COMMON_CHIP_VALUES = [50, 60, 80, 100, 120];

const getChipPresetKey = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return "";
  return COMMON_CHIP_VALUES.includes(parsed) ? `chip${parsed}` : "custom";
};

function EditFormulasByTypeForm({
  localId,
  localMachineDetails,
  onClose,
  onSuccess,
  getType,
  isFichaType,
  getFormulaName,
  getFormulaValue,
  money,
}) {
  const [formulaForms, setFormulaForms] = React.useState({});
  const [saving, setSaving] = React.useState(false);
  const [effectiveDate, setEffectiveDate] = React.useState(getDefaultOperationDate());
  const [generalError, setGeneralError] = React.useState("");
  const [latestAffectedMachineCollectionDate, setLatestAffectedMachineCollectionDate] =
    React.useState(null);

  const availableTypes = React.useMemo(() => {
    const set = new Set();
    (localMachineDetails || []).forEach((lm) => {
      const label = getType(lm);
      if (isFichaType(label) && !hasRent(lm)) set.add(label);
    });
    return Array.from(set);
  }, [localMachineDetails, getType, isFichaType]);

  const currentFormulaByType = React.useMemo(() => {
    const map = {};
    (localMachineDetails || []).forEach((lm) => {
      const label = getType(lm);
      if (!isFichaType(label) || hasRent(lm)) return;

      if (!map[label]) {
        const name = getFormulaName(lm);
        const value = getFormulaValue(lm);
        map[label] = { name, value };
      }
    });
    return map;
  }, [localMachineDetails, getType, isFichaType, getFormulaName, getFormulaValue]);

  const affectedLocalMachines = React.useMemo(
    () =>
      (localMachineDetails || []).filter((lm) => {
        const label = getType(lm);
        return isFichaType(label) && !hasRent(lm);
      }),
    [localMachineDetails, getType, isFichaType]
  );

  const minEffectiveDate = React.useMemo(() => {
    let latest = null;

    affectedLocalMachines.forEach((lm) => {
      const parsed = parseDateOnlyToLocalDate(dateOnlyFromDateTime(lm?.StartDateTime));
      if (parsed && (!latest || parsed > latest)) {
        latest = parsed;
      }
    });

    return latest;
  }, [affectedLocalMachines]);

  React.useEffect(() => {
    let cancelled = false;

    const loadLatestAffectedMachineCollectionDate = async () => {
      if (!affectedLocalMachines.length) {
        if (!cancelled) setLatestAffectedMachineCollectionDate(null);
        return;
      }

      try {
        const results = await Promise.all(
          affectedLocalMachines.map((lm) =>
            VisitTransactionService.getLastCollectionByMachineInLocal(lm.Id)
          )
        );

        let latest = null;
        results.forEach((result) => {
          const parsed = parseDateOnlyToLocalDate(
            dateOnlyFromDateTime(result?.visit?.DateTime)
          );
          if (parsed && (!latest || parsed > latest)) {
            latest = parsed;
          }
        });

        if (!cancelled) {
          setLatestAffectedMachineCollectionDate(latest);
        }
      } catch (error) {
        if (process.env.NODE_ENV !== "production") {
          console.error("Error al obtener la última recaudación de las máquinas afectadas:", error);
        }
        if (!cancelled) {
          setLatestAffectedMachineCollectionDate(null);
        }
      }
    };

    loadLatestAffectedMachineCollectionDate();

    return () => {
      cancelled = true;
    };
  }, [affectedLocalMachines]);

  const pickerMinDate = React.useMemo(() => {
    if (!minEffectiveDate) return latestAffectedMachineCollectionDate;
    if (!latestAffectedMachineCollectionDate) return minEffectiveDate;
    return latestAffectedMachineCollectionDate > minEffectiveDate
      ? latestAffectedMachineCollectionDate
      : minEffectiveDate;
  }, [latestAffectedMachineCollectionDate, minEffectiveDate]);

  const getEffectiveDateErrorMessage = React.useCallback(() => {
    if (!isValidOperationDate(effectiveDate)) {
      return "Selecciona una fecha válida.";
    }
    if (isFutureOperationDate(effectiveDate)) {
      return "No se puede seleccionar una fecha futura.";
    }
    if (
      latestAffectedMachineCollectionDate &&
      isBeforeMinOperationDate(effectiveDate, latestAffectedMachineCollectionDate)
    ) {
      return "La fecha seleccionada no puede ser anterior a la última recaudación de las máquinas afectadas.";
    }
    if (minEffectiveDate && isBeforeMinOperationDate(effectiveDate, minEffectiveDate)) {
      return "La fecha seleccionada no puede ser anterior al último movimiento de las máquinas afectadas.";
    }
    return "";
  }, [effectiveDate, latestAffectedMachineCollectionDate, minEffectiveDate]);

  const getApiErrorMessage = React.useCallback((error) => {
    const status = error?.response?.status;
    const code = error?.response?.data?.code || "";
    const message =
      error?.response?.data?.error ||
      "No se pudieron actualizar las fórmulas por tipo. Intenta nuevamente.";

    if (
      status === 409 &&
      code === "EFFECTIVE_DATE_BEFORE_CURRENT_LOCAL_MACHINE_START"
    ) {
      return "La fecha seleccionada no puede ser anterior al último movimiento de las máquinas afectadas.";
    }
    if (status === 409 && code === "EFFECTIVE_DATE_BEFORE_LAST_MACHINE_COLLECTION") {
      return "La fecha seleccionada no puede ser anterior a la última recaudación de las máquinas afectadas.";
    }
    if (status === 400 && code === "EFFECTIVE_DATE_CANNOT_BE_FUTURE") {
      return "No se puede seleccionar una fecha futura.";
    }
    return message;
  }, []);

  const handlePresetChipValue = (typeKey, value) => {
    setGeneralError("");
    setFormulaForms((prev) => ({
      ...prev,
      [typeKey]: {
        Mode: "preset",
        Type: "*",
        Value: String(value),
      },
    }));
  };

  const handleToggleAdjust = (typeKey) => {
    setGeneralError("");
    setFormulaForms((prev) => {
      const currentFormula = currentFormulaByType[typeKey] || {};
      const currentValue =
        currentFormula?.value === null || currentFormula?.value === undefined
          ? ""
          : String(currentFormula.value);

      return {
        ...prev,
        [typeKey]: {
          Mode: "custom",
          Type: "*",
          Value: prev[typeKey]?.Value ?? currentValue,
        },
      };
    });
  };

  const handleCustomChipValueChange = (typeKey, value) => {
    setGeneralError("");
    setFormulaForms((prev) => ({
      ...prev,
      [typeKey]: {
        Mode: "custom",
        Type: "*",
        Value: value,
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!availableTypes.length) {
      onClose?.();
      return;
    }

    const effectiveDateError = getEffectiveDateErrorMessage();
    if (effectiveDateError) {
      return;
    }

    try {
      setSaving(true);
      setGeneralError("");

      const updatesPerType = [];

      availableTypes.forEach((typeLabel) => {
        const form = formulaForms[typeLabel] || {};
        const currentValue = currentFormulaByType[typeLabel]?.value;
        const rawValue = form.Value;

        if (rawValue == null || String(rawValue).trim() === "") return;

        const nextValue = Number(rawValue);
        if (!Number.isFinite(nextValue) || nextValue <= 0) return;
        if (currentValue != null && Number(currentValue) === nextValue) return;

        updatesPerType.push({ typeLabel, Type: "*", Value: nextValue });
      });

      if (updatesPerType.length === 0) {
        onClose?.();
        return;
      }

      for (const upd of updatesPerType) {
        const { typeLabel, Type, Value } = upd;

        const targetMachines = (localMachineDetails || []).filter(
          (lm) => !hasRent(lm) && getType(lm) === typeLabel
        );

        const createdFormula = await FormulaService.createFormula({ Type, Value });
        const newFormulaId = createdFormula?.Id ?? createdFormula ?? null;
        if (newFormulaId == null) continue;

        for (const lm of targetMachines) {
          const payload = {
            FormulaId: Number(newFormulaId),
            IsAccumulative: false,
            EffectiveDate: toLocalDateOnlyString(effectiveDate),
          };

          await LocalMachineService.updateLocalMachine(lm.Id, payload);
        }
      }

      await onSuccess?.();
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        console.error("Error al actualizar fórmulas por tipo:", err);
      }
      setGeneralError(getApiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  if (availableTypes.length === 0) {
    return (
      <Box sx={{ p: 1 }}>
        <MDTypography variant="body2">
          No hay máquinas activas de tipo Pool, Tejo o Peluchera en este local.
        </MDTypography>
      </Box>
    );
  }

  const effectiveDateError = getEffectiveDateErrorMessage();
  const today = getDefaultOperationDate();

  return (
    <form onSubmit={handleSubmit} noValidate>
      <Box sx={{ mt: 1, minWidth: 0 }}>
        {availableTypes.map((typeLabel) => {
          const currentFormula = currentFormulaByType[typeLabel] || {};
          const form = formulaForms[typeLabel] || {};
          const visibleValue =
            form.Value !== undefined
              ? form.Value
              : currentFormula?.value !== null && currentFormula?.value !== undefined
                ? String(currentFormula.value)
                : "";

          const currentPreset = getChipPresetKey(currentFormula?.value);
          const formPreset = form.Value !== undefined ? getChipPresetKey(form.Value) : "";

          const showAdjust =
            form.Mode === "custom" ||
            (form.Mode === undefined && currentPreset === "custom");

          const selectedPreset = showAdjust
            ? "custom"
            : formPreset || currentPreset;

          return (
            <Box
              key={typeLabel}
              sx={{ mb: 3, pb: 2, borderBottom: "1px solid rgba(0,0,0,0.08)" }}
            >
              <AppFormContext
                title={typeLabel}
                lines={[
                  `Precio de ficha actual: ${currentFormula?.value == null ? "Sin configurar" : `$ ${currentFormula.value}`}`,
                ]}
                sx={{ borderBottom: 0, pb: 0, mb: 1 }}
              />

              <Box sx={{ display: "flex", justifyContent: "center", mb: showAdjust ? 2 : 0 }}>
                <ButtonGroup variant="outlined" size="small" sx={segmentedButtonSx}>
                  {COMMON_CHIP_VALUES.map((value) => (
                    <Button
                      key={value}
                      type="button"
                      variant={selectedPreset === `chip${value}` ? "contained" : "outlined"}
                      onClick={() => handlePresetChipValue(typeLabel, value)}
                    >
                      $ {value}
                    </Button>
                  ))}
                  <Button
                    type="button"
                    variant={showAdjust ? "contained" : "outlined"}
                    onClick={() => handleToggleAdjust(typeLabel)}
                  >
                    Ajustar
                  </Button>
                </ButtonGroup>
              </Box>

              {showAdjust && (
                <Grid container spacing={2} justifyContent="center">
                  <Grid item xs={12} md={6}>
                    <AppNumberInput
                      fullWidth
                      label="Precio de ficha"
                      value={visibleValue}
                      min={1}
                      decimalScale={0}
                      inputProps={{ step: 1 }}
                      onValueChange={(cleanValue) =>
                        handleCustomChipValueChange(typeLabel, cleanValue)
                      }
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      }}
                    />
                  </Grid>
                </Grid>
              )}
            </Box>
          );
        })}

        <Box sx={{ mb: 3 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={esLocale}>
            <DatePicker
              label="Fecha de modificación"
              value={effectiveDate}
              onChange={(date) => {
                setEffectiveDate(date);
                setGeneralError("");
              }}
              minDate={pickerMinDate || undefined}
              maxDate={today}
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: true,
                  error: Boolean(effectiveDateError),
                  helperText: effectiveDateError,
                },
              }}
            />
          </LocalizationProvider>
        </Box>

        {generalError && (
          <MDTypography variant="body2" color="error" sx={{ mb: 2 }}>
            {generalError}
          </MDTypography>
        )}

        <Box sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <CancelButton onClick={onClose} disabled={saving} fullWidth>
                Cancelar
              </CancelButton>
            </Grid>
            <Grid item xs={12} md={6}>
              <AcceptButton type="submit" disabled={saving || Boolean(effectiveDateError)} fullWidth>
                Guardar cambios
              </AcceptButton>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </form>
  );
}

export default EditFormulasByTypeForm;
